/**
 * PasswordResetGuard — layout wrapper that redirects to /force-password-reset
 * if the current user has must_change_password=true (D1 provisioning flow).
 *
 * Mount this around every protected route group. It is a no-op for users who
 * don't have the flag set. Uses a short-lived query so the redirect fires
 * before the first protected page renders.
 */
import React, { useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { useAuth } from '@/context';
import { api } from '@core/api/client';

interface PasswordStatusResponse {
  must_change_password: boolean;
}

interface PasswordResetGuardProps {
  children: React.ReactNode;
}

export const FORCE_RESET_PATH = '/force-password-reset';
export const PASSWORD_STATUS_KEY = ['auth', 'password-status'] as const;

const PasswordResetGuard: React.FC<PasswordResetGuardProps> = ({ children }) => {
  const { isAuthenticated, user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const { data } = useQuery<PasswordStatusResponse>({
    queryKey: PASSWORD_STATUS_KEY,
    queryFn: () => api.get<PasswordStatusResponse>('/v1/auth/password-status'),
    enabled: isAuthenticated && !!user,
    staleTime: 60_000, // 1 min — enough for a session; invalidated after reset
    retry: false, // don't retry on 401 (shouldn't happen inside ProtectedRoute)
  });

  useEffect(() => {
    if (
      data?.must_change_password &&
      location.pathname !== FORCE_RESET_PATH
    ) {
      navigate(FORCE_RESET_PATH, { replace: true });
    }
  }, [data, location.pathname, navigate]);

  return <>{children}</>;
};

export default PasswordResetGuard;

